import React, { useState, useEffect, useRef } from "react";
import { IReducer } from "../../state/reducer";
import "./InventoryRow.css";
import { useCanvas } from "../GlobalCanvasManager/CanvasContext";
import { getModelPath } from "../../utils/brickModelPaths";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { calculateBrickPackAutoCollect } from "../../utils/calculateBrickPackAutoCollect";

interface InventoryEntryProps extends IReducer {
  brickId: string;
}

const InventoryRow: React.FC<InventoryEntryProps> = ({ state, brickId }) => {
  const [showPopup, setShowPopup] = useState(false);
  const currBrick = state.bricks.get(brickId)!;
  const currAmount = currBrick.amount;
  const currCapacity = currBrick.capacity;
  const currAuto = currBrick.autoCollect;

  // Calculate brick pack auto-collect contribution
  const packAutoCollect = calculateBrickPackAutoCollect(brickId, state);

  const containerRef = useRef<HTMLDivElement>(null);
  const visualRef = useRef<HTMLSpanElement>(null);
  const { registerCard, unregisterCard } = useCanvas();

  // Drag and drop functionality
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: brickId, disabled: !state.editModeInventory });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: state.editModeInventory ? "grab" : "pointer",
  };

  // Register this card with the global canvas manager (only once on mount)
  useEffect(() => {
    if (!visualRef.current) return;

    const cardId = `inventory-row-${brickId}`;
    const modelPath = getModelPath(brickId);

    // Get actual dimensions from the DOM element
    const rect = visualRef.current.getBoundingClientRect();

    registerCard({
      id: cardId,
      modelPath,
      element: visualRef.current,
      width: rect.width,
      height: rect.height,
    });

    return () => {
      unregisterCard(cardId);
    };
    // Only run once on mount - don't re-register when amounts change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brickId]);

  return (
    <div
      ref={(node) => {
        containerRef.current = node;
        setNodeRef(node);
      }}
      style={style}
      {...attributes}
      {...listeners}
      className={`${Number.isNaN(currAmount) ? "inventoryRow hideEntry" : "inventoryRow"} ${state.editModeInventory ? "editMode" : ""} ${isDragging ? "dragging" : ""}`}
      onMouseEnter={
        state.editModeInventory ? undefined : () => setShowPopup(true)
      }
      onMouseLeave={
        state.editModeInventory ? undefined : () => setShowPopup(false)
      }
    >
      <span className="invRowVisual" ref={visualRef}>
        {/* 3D model rendered by GlobalCanvasManager - visualRef stays stable */}
      </span>
      <div className="inventoryRowParent">
        <div className="inventoryRowDetails">
          <b className="invRowName">
            {currBrick?.name ? currBrick.name : "Default"}
          </b>
          <b className="">
            {currAmount} / {currCapacity}
          </b>
          <b className="invRowAuto">
            {/* Show individual auto-collect if exists */}
            {currAuto > 0 && `${currAuto} /s`}
            {/* Show separator if both exist */}
            {currAuto > 0 && packAutoCollect.totalAutoPurchases > 0 && " + "}
            {/* Show pack auto-collect with percentage if exists */}
            {packAutoCollect.totalAutoPurchases > 0 &&
              `${packAutoCollect.totalAutoPurchases} /s (${packAutoCollect.averageFindPercentage.toFixed(0)}%)`}
            {/* Show 0 /s with percentage if brick is part of pack but no auto-collect */}
            {currAuto === 0 &&
              packAutoCollect.totalAutoPurchases === 0 &&
              packAutoCollect.isPartOfPack &&
              `0 /s (${packAutoCollect.overallFindPercentage.toFixed(0)}%)`}
            {/* Show 0 /s if not part of any pack */}
            {currAuto === 0 &&
              packAutoCollect.totalAutoPurchases === 0 &&
              !packAutoCollect.isPartOfPack &&
              "0 /s"}
          </b>
        </div>
      </div>
      {showPopup && !state.editModeInventory && (
        <div className="inventoryPopup">
          <div>Additional info about title</div>
          <div>Additional info about title</div>
          <div>Additional info about title</div>
        </div>
      )}
    </div>
  );
};

export default InventoryRow;
